'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
  details: string[];
}

export default function BookingReservationsSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'How do I make a reservation at Proximus?',
      answer: 'You can book through our website, phone, or email. Online booking provides instant confirmation and best rates.',
      details: [
        'Visit our Booking Hub and select your dates and apartment category',
        'Call us at +91-72593 12233 for personalized assistance',
        'Email reservations@proximusapartments.com with your requirements',
        'Corporate clients can use our dedicated booking portal with special rates',
      ],
    },
    {
      question: 'Can I modify or cancel my reservation?',
      answer: 'Yes, modifications and cancellations are allowed based on our flexible cancellation policy with varying refund percentages.',
      details: [
        '30+ days before check-in: 100% refund minus processing fee',
        '15-29 days before: 75% refund of total booking amount',
        '7-14 days before: 50% refund of total booking amount',
        'Less than 7 days: No refund - full amount charged',
        'Modifications subject to availability and rate differences',
      ],
    },
    {
      question: 'Do you offer corporate booking accounts?',
      answer: 'Yes, we provide dedicated corporate accounts with special rates, flexible billing, and priority booking.',
      details: [
        'Volume-based discounts for regular bookings',
        'Monthly consolidated billing with 30-day payment terms',
        'Dedicated account manager for seamless coordination',
        'Priority booking and express check-in/check-out',
        'Customized reporting for expense management',
      ],
    },
    {
      question: 'Can I book for someone else?',
      answer: 'Yes, you can make reservations for others. The guest must present valid ID at check-in.',
      details: [
        'Provide guest name and contact details during booking',
        'Guest must be 18+ years old with valid government-issued ID',
        'Payment can be made by the booking person or guest',
        'Corporate bookings can include multiple guest names',
      ],
    },
    {
      question: 'How do I request a group booking?',
      answer: 'Group bookings (3+ apartments) require advance notice and can be arranged through our reservations team.',
      details: [
        'Contact us at least 14 days in advance for group bookings',
        'Special group rates available for 5+ apartments',
        'Flexible payment options including staggered payments',
        'Dedicated coordinator for group logistics',
      ],
    },
  ];

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="booking-reservations" className="py-16 lg:py-24 bg-background">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="CalendarIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Booking & Reservations</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Reservation Questions
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Everything you need to know about making, modifying, and managing your reservations at Proximus.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-display text-xl font-semibold text-primary mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-text-secondary text-sm">{faq.answer}</p>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 animate-slide-in-left">
                  <div className="border-t border-border pt-4">
                    <ul className="space-y-3">
                      {faq.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-accent flex-shrink-0 mt-0.5"
                          />
                          <span className="text-text-secondary text-sm leading-relaxed">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}